import axios from 'axios';
import { getCurrentInstance, ref, watch, onBeforeMount, onBeforeUnmount } from 'vue';
import { useQuillInlineStyles } from '@/composables/common/useQuillInlineStyles';

export function useQuillEditor(props, emit) {
    const { convertQuillToInline } = useQuillInlineStyles();
    const instance = getCurrentInstance();
    const toast = instance?.proxy?.$toast;

    const quillEditorRef = ref(null);
    const internalContent = ref(props.value);
    const hasChanges = ref(false);
    // `processAndGetHTML` se usa también desde `onBeforeUnmount`; este flag evita que
    // durante el desmontaje se reprograme una restauración de foco/scroll sobre un
    // editor que ya está saliendo del DOM.
    const isUnmounting = ref(false);
    const imageStorageBaseUrl = 'https://storage.compralaentrada.com/';

    const editorOption = {
        modules: {
            toolbar: [
                [{ header: [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                ['blockquote', 'code-block'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                [{ script: 'sub' }, { script: 'super' }],
                [{ indent: '-1' }, { indent: '+1' }],
                [{ direction: 'rtl' }],
                [{ size: ['small', false, 'large', 'huge'] }],
                [{ color: [] }, { background: [] }],
                [{ font: [] }],
                [{ align: [] }],
                ['clean'],
                ['link', 'image', 'video'],
            ],
        },
        theme: props.theme,
        placeholder: props.placeholder,
        readOnly: props.disabled,
        ...props.options,
    };

    watch(
        () => props.value,
        newVal => {
            if (newVal !== internalContent.value) {
                internalContent.value = newVal;
            }
        },
        { immediate: true }
    );

    watch(
        () => props.disabled,
        newVal => {
            editorOption.readOnly = newVal;
        }
    );

    const extractUploadedPath = payload => {
        if (typeof payload === 'string') {
            return payload;
        }

        return payload?.path || payload?.url || payload?.data?.path || payload?.data?.url || '';
    };

    const resolveImageUrl = uploadedPath => {
        if (typeof uploadedPath !== 'string' || uploadedPath.trim() === '') {
            throw new Error('La API de subida no ha devuelto una ruta valida');
        }

        if (/^https?:\/\//i.test(uploadedPath)) {
            return uploadedPath;
        }

        return `${imageStorageBaseUrl}${uploadedPath.replace(/^\/+/, '')}`;
    };

    const uploadImage = async file => {
        if (!props.imageUploadUrl) {
            throw new Error('No se ha configurado la URL de subida de imagenes');
        }

        const formData = new FormData();
        formData.append('file', file);

        const teatroHash = localStorage.getItem('hash_teatro');
        const headers = {
            'Content-Type': 'multipart/form-data'
        };

        if (teatroHash) {
            headers['Hash-Teatro'] = teatroHash;
        }

        const response = await axios.post(props.imageUploadUrl, formData, {
            headers
        });

        return resolveImageUrl(extractUploadedPath(response.data));
    };

    const selectImageFile = () => new Promise(resolve => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';

        input.addEventListener('change', () => {
            resolve(input.files?.[0] || null);
        }, { once: true });

        input.click();
    });

    const insertUploadedImage = async quill => {
        const file = await selectImageFile();

        if (!file) {
            return;
        }

        if (!file.type?.startsWith('image/')) {
            toast?.error('El archivo seleccionado no es una imagen valida');
            return;
        }

        try {
            const imageUrl = await uploadImage(file);
            const range = quill.getSelection(true);
            const index = range?.index ?? quill.getLength();

            quill.insertEmbed(index, 'image', imageUrl, 'user');
            quill.setSelection(index + 1, 0, 'silent');
        } catch (error) {
            toast?.error(error.response?.data?.message || 'No se ha podido subir la imagen');
        }
    };

    const setupImageHandler = quill => {
        if (!props.imageUploadUrl) {
            return;
        }

        const toolbar = quill?.getModule('toolbar');

        if (!toolbar) {
            return;
        }

        toolbar.addHandler('image', () => {
            return insertUploadedImage(quill);
        });
    };

    const onEditorBlur = quill => emit('blur', quill);
    const onEditorFocus = quill => emit('focus', quill);
    // Quill enfoca el editor al pegar el contenido inicial y el navegador
    // arrastra el scroll hasta el elemento enfocado. En el formulario de crear
    // evento eso hace que la página salte hacia abajo al cargar, porque el
    // `synopsis` llega pre-rellenado y `vue-quill-editor` llama `pasteHTML` en su
    // `mounted` (ver node_modules/vue-quill-editor/src/editor.vue). Con
    // `scroll-behavior: smooth` global (assets/adminlte) el salto además se anima.
    //
    // El focus y el scroll los dispara el `mounted` de Quill, antes de que se emita
    // `ready`, así que la posición se captura en `onBeforeMount` —que corre antes del
    // `mounted` del hijo— y se restaura en el frame siguiente.
    //
    // El `blur()` NO basta, y la posición NO se puede inferir del `scrollY`: con
    // `scroll-behavior: smooth` el scroll nativo por foco es una animación diferida,
    // así que en Chromium el `scrollY` todavía vale lo mismo que antes cuando corre
    // este frame (la animación aún no ha empezado). Además `blur()` no cancela esa
    // animación: sólo la cancela un `scrollTo` con `behavior: 'instant'`. Por eso se
    // restaura SIEMPRE que el foco haya sido robado, sin comparar posiciones, y se
    // re-afirma un frame después por si la animación reintenta (en Firefox el scroll
    // ya está aplicado de forma síncrona, así que ambos frames convergen al mismo sitio).
    //
    // Nunca se roba el foco al usuario: sólo se revierte si el foco terminó dentro
    // de este editor, que es exactamente el caso del enfoque automático de Quill.
    const scrollBeforeMount = ref(null);

    onBeforeMount(() => {
        scrollBeforeMount.value = typeof window !== 'undefined' ? window.scrollY : null;
    });

    const restoreScrollAfterMount = quill => {
        const editorEl = quill?.root;
        if (!editorEl || scrollBeforeMount.value === null) {
            return;
        }

        const previousScroll = scrollBeforeMount.value;
        scrollBeforeMount.value = null;

        const restoreScroll = () => {
            window.scrollTo({ top: previousScroll, left: 0, behavior: 'instant' });
        };

        // `requestAnimationFrame` deja que el navegador aplique su propio scroll de
        // "traer el elemento enfocado a la vista" antes de deshacerlo.
        requestAnimationFrame(() => {
            const active = document.activeElement;
            const focusIsInThisEditor = active === editorEl
                || (active && editorEl.contains(active));

            if (!focusIsInThisEditor) {
                // El foco lo tiene otra cosa (el usuario tecleando, u otro campo):
                // no se toca ni el foco ni el scroll.
                return;
            }

            editorEl.blur();
            restoreScroll();

            // Segundo intento: `scroll-behavior: smooth` puede reintentar la
            // animación después de este frame.
            requestAnimationFrame(restoreScroll);
        });
    };

    const onEditorReady = quill => {
        setupImageHandler(quill);
        restoreScrollAfterMount(quill);
        emit('ready', quill);
    };

    const onEditorChange = ({ quill, html, text }) => {
        internalContent.value = html;
        hasChanges.value = true;
        emit('input', html);
        emit('change', { quill, html, text });
    };

    /**
     * Devuelve el HTML del editor (con estilos inline) y sincroniza el estado interno.
     *
     * Ojo con el `v-model`: asignar `internalContent` cambia el `value` del
     * `quill-editor` interno y su watcher llama `pasteHTML`, que ENFOCA el editor.
     * En el submit eso roba el foco del botón "CONTINUAR" y el navegador arrastra el
     * scroll hasta el editor — el mismo mecanismo que el salto al montar, pero
     * disparado al guardar. Por eso se capturan foco y scroll ANTES de asignar y se
     * devuelven un frame después, y sólo cuando el foco estaba FUERA del editor (si el
     * usuario está escribiendo dentro, no se toca nada).
     *
     * @returns {string} HTML procesado, o el interno si no hay cambios que procesar
     */
    const processAndGetHTML = () => {
        if (!props.useInlineStyles || !hasChanges.value) {
            return internalContent.value;
        }

        const editorEl = quillEditorRef.value?.quill?.root;
        const activeBefore = typeof document !== 'undefined' ? document.activeElement : null;
        const scrollBefore = typeof window !== 'undefined' ? window.scrollY : 0;
        const focusWasInsideEditor = !!editorEl
            && (activeBefore === editorEl || editorEl.contains(activeBefore));

        const rawHtml =
            quillEditorRef.value?.quill?.root?.innerHTML || internalContent.value;
        const processedHtml = convertQuillToInline(rawHtml);

        internalContent.value = processedHtml;
        hasChanges.value = false;

        if (editorEl && !focusWasInsideEditor) {
            const restoreFocusAndScroll = () => {
                if (activeBefore && typeof activeBefore.focus === 'function'
                    && document.contains(activeBefore)) {
                    activeBefore.focus({ preventScroll: true });
                }
                window.scrollTo({ top: scrollBefore, left: 0, behavior: 'instant' });
            };

            // `requestAnimationFrame` puede no existir (jsdom) y en el desmontaje no
            // tiene sentido reprogramar nada: el editor ya no está en el DOM.
            if (typeof requestAnimationFrame === 'function' && !isUnmounting.value) {
                requestAnimationFrame(() => {
                    restoreFocusAndScroll();
                    requestAnimationFrame(restoreFocusAndScroll);
                });
            }
        }

        return processedHtml;
    };

    const getQuill = () => quillEditorRef.value?.quill;
    const getHTML = () => quillEditorRef.value?.quill?.root?.innerHTML || internalContent.value;
    const getText = () => quillEditorRef.value?.quill?.getText();
    const getLength = () => quillEditorRef.value?.quill?.getLength();

    const insertText = (index, text, formats = {}) =>
        quillEditorRef.value?.quill?.insertText(index, text, formats);
    const deleteText = (index, length) =>
        quillEditorRef.value?.quill?.deleteText(index, length);
    const formatText = (index, length, formats) =>
        quillEditorRef.value?.quill?.formatText(index, length, formats);

    onBeforeUnmount(() => {
        isUnmounting.value = true;

        if (hasChanges.value && props.useInlineStyles) {
            const processedHtml = processAndGetHTML();
            emit('input', processedHtml);
        }
    });

    return {
        quillEditorRef,
        internalContent,
        editorOption,
        onEditorBlur,
        onEditorFocus,
        onEditorReady,
        onEditorChange,
        processAndGetHTML,
        getQuill,
        getHTML,
        getText,
        getLength,
        insertText,
        deleteText,
        formatText,
    };
}
